<template>
  <div
    class="wrapper"
  >
    <slot
      name="before"
      :submit-tag="submitTag"
    />
    <TagAutocompletion
      :items="tagAutocompleteSuggestions"
      :is-async="true"
      @focus-empty="showStoredTagSuggestions()"
      @toggle-favorite="toggleFavoriteSuggestion"
      @update:model-value="updateTagAutocompletionSuggestions"
      @submit="submitTag()"
      v-model="newTag"
      class="autocomplete"
    />
    <slot
      name="after"
      :submit-tag="submitTag"
    >
      <a
        @click="submitTag()"
        aria-label="Save tag"
        title="Save tag"
        style="cursor:pointer;"
      >
        <Save
          :size="16"
          :color="textColor"
        />
      </a>
    </slot>
  </div>
</template>

<script lang="ts">
export type Emits = {
  (e: "add", tag: string): void;
};
</script>
<script setup lang="ts">
/* eslint-disable import/first */
import { defineEmits, ref, watch } from "vue";
import { Save } from "lucide-vue-next";
import TagAutocompletion from "@/components/TagAutocompletion.vue";
import { useThemeColor } from "@/composables/useThemeColor";
import type { TagAutocompleteItem } from "@/components/tagAutocompletion";
import type { SearchTagResult, SearchTagResultTag } from "@/stores/ImageMetadata";
import { buildUrlWithParams } from "@/requests/url";
import { useApiStore } from "@/stores/api";
import { useFetch } from "@/libraries/vue-fetch/composables/useFetch";
import { useRequestTrackerStore } from "@/libraries/vue-fetch/stores/requestTracker";
import { useFavoriteTagsStore } from "@/stores/favoriteTags";
import { useRecentTagsStore } from "@/stores/recentTags";
/* eslint-enable import/first */

const apiConfig = useApiStore();
const favoriteTags = useFavoriteTagsStore();
const recentTags = useRecentTagsStore();
const textColor = useThemeColor();

const emit = defineEmits<Emits>();

const newTag = ref<string>("");
const tagAutocompleteSuggestions = ref<TagAutocompleteItem[]>([]);

let lastRequest: null | string = null;
const requestTracker = useRequestTrackerStore();

function buildStoredTagSuggestions(): TagAutocompleteItem[] {
  const favSet = new Set(favoriteTags.tags);
  const seenSet = new Set<string>();

  // Determine favorite order based on sort mode
  const favoritesInOrder = apiConfig.favoriteTagSortMode === "recent"
    ? [...favoriteTags.tags].sort((a, b) => {
        // Sort by recent first appearance (earlier in recent list = more recent)
        const aIndex = recentTags.tags.indexOf(a);
        const bIndex = recentTags.tags.indexOf(b);
        // If not in recent, put at the end (not seen recently)
        const aPos = aIndex === -1 ? Infinity : aIndex;
        const bPos = bIndex === -1 ? Infinity : bIndex;
        return aPos - bPos;
      })
    : favoriteTags.tags; // Keep fixed order

  // Add favorites first (in the determined order)
  const favItems: TagAutocompleteItem[] = favoritesInOrder.map((tag) => {
    seenSet.add(tag);
    return {
      value: tag,
      meta: "Stored tag",
      canToggleFavorite: true,
      isFavorite: true,
    };
  });

  // Add recent tags that aren't already in favorites
  const recentItems: TagAutocompleteItem[] = recentTags.tags
    .filter((tag) => !seenSet.has(tag))
    .map((tag) => ({
      value: tag,
      meta: "Recent tag",
      canToggleFavorite: true,
      isFavorite: favSet.has(tag), // Mark if it was added to favorites
    }));

  return [...favItems, ...recentItems];
}

function showStoredTagSuggestions() {
  tagAutocompleteSuggestions.value = buildStoredTagSuggestions();
}

function buildSearchTagSuggestion(tag: SearchTagResultTag): TagAutocompleteItem {
  return {
    value: tag.value,
    meta: `${tag.count.toLocaleString()} matches`,
    canToggleFavorite: true,
    isFavorite: favoriteTags.isFavorite(tag.value),
  };
}

watch(
  () => favoriteTags.tags.slice(),
  () => {
    if (newTag.value === "") {
      showStoredTagSuggestions();
      return;
    }
    tagAutocompleteSuggestions.value = tagAutocompleteSuggestions.value.map((item) => ({
      ...item,
      isFavorite: favoriteTags.isFavorite(item.value),
    }));
  },
  { immediate: true },
);

async function updateTagAutocompletionSuggestions(newValue: string) {
  console.log("Root: On Change - before api", { newValue });

  if (!newValue) {
    showStoredTagSuggestions();
    return;
  }

  let suggestionData;
  try {
    suggestionData = await fetchAutocompleteSuggestions(newValue); // DANGER! This will throw an exception now if this request gets aborted by starting a new one!!!
  } catch (e) {
    console.error("Root: On Change - api error", { newValue, e });
    return;
  }
  console.log("Root: On Change - after api", { suggestionData });
  const suggestions = suggestionData.map(buildSearchTagSuggestion);
  tagAutocompleteSuggestions.value = suggestions;
  console.log("Root: On Change - after api + write", {
    newValue,
    suggestions,
    tagAutocompleteOptions: JSON.stringify(tagAutocompleteSuggestions.value),
  });
}

/**
 * @throws Error if this request gets aborted by starting a new one!!!
 * @param search
 */
async function fetchAutocompleteSuggestions(
  search: string,
): Promise<SearchTagResultTag[]> {
  console.log("Root: requesting autocomplete:", search);
  if (lastRequest !== null) {
      try {
        requestTracker.getAbortController(lastRequest).abort();
      } catch (e) {
        // Already aborted
      }
  }
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  if (apiConfig.voteServiceIds === null) {
    throw Error("voteServiceIds can't be null");
  }
  const url = buildUrlWithParams(
    apiConfig.host + "/add_tags/search_tags",
    {
      search,
      // file domain (optional, defaults to 'all my files'),
      // tag_service_key: (optional, hexadecimal, the tag domain on which to search, defaults to 'all known tags')
      // tag_display_type: 'storage' | 'display' | undefined (optional, string, to select whether to search raw or sibling-processed tags, defaults to 'storage')
    },
  );
  const { requestId, fetch } = useFetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Hydrus-Client-API-Session-Key": apiConfig.sessionKey,
    },
  });
  lastRequest = requestId; // prime the abort controller
  const response = await fetch; // DANGER! This will throw an exception now if this request gets aborted by starting a new one!!!
  const data = <SearchTagResult> await response.json();
  console.log("Root: retrieved autocomplete:", data);
  return data.tags;
}

async function toggleFavoriteSuggestion(tag: string) {
  favoriteTags.toggle(tag);
  if (newTag.value === "") {
    showStoredTagSuggestions();
    return;
  }
  tagAutocompleteSuggestions.value = tagAutocompleteSuggestions.value.map((item) =>
    item.value === tag
      ? {
          ...item,
          isFavorite: favoriteTags.isFavorite(tag),
        }
      : item,
  );
}

async function submitTag() {
  const tag = newTag.value;
  emit("add", tag);
  if (!favoriteTags.isFavorite(tag)) {
    recentTags.record(tag);
  }
  if (newTag.value === tag) {
    newTag.value = ""; // make empty for the next tag.
    showStoredTagSuggestions();
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  width: 100%;
}
input {
  flex-grow: 2;
  flex-shrink: 2;
}
a {
  flex-grow: 0;
  flex-shrink: 0;
  padding-left: 0.5em;
}
.autocomplete {
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
}
</style>
